import { useAuth } from '@clerk/react-router';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { advanceSessionEpoch, getSessionEpoch } from './sessionBoundary';

type GetTokenFn = (options?: { skipCache?: boolean }) => Promise<string | null>;

/**
 * Token Getter Component
 *
 * Initializes tokenGetter with Clerk's getToken function
 * This enables API calls to include authenticated tokens
 */
export const TokenGetter = () => {
  const { getToken, userId } = useAuth();
  const previousUserId = useRef<string | null | undefined>(userId);

  // A layout effect, so the boundary is crossed before the browser paints and before any
  // passive effect or pending token promise of the new render can run.
  useLayoutEffect(() => {
    // Leaving an identity (sign-out, or switching straight to another user) is a
    // session boundary, so requests and token refreshes begun for it cannot be
    // completed under the next one. Arriving from signed-out is not: nothing
    // authenticated is in flight yet.
    if (previousUserId.current && previousUserId.current !== userId) {
      advanceSessionEpoch();
    }
    previousUserId.current = userId;
  }, [userId]);

  useEffect(() => {
    const instance = ClerkTokenGetter.getInstance();
    instance.setTokenFunction((options) => getToken(options));
  }, [getToken]);

  return null;
};

class ClerkTokenGetter {
  private static instance: ClerkTokenGetter;
  private getTokenFn: GetTokenFn | null = null;
  private freshTokenFlight: { epoch: number; promise: Promise<string | null> } | null = null;

  // private constructor() { }

  public static getInstance(): ClerkTokenGetter {
    if (!ClerkTokenGetter.instance) {
      ClerkTokenGetter.instance = new ClerkTokenGetter();
    }
    return ClerkTokenGetter.instance;
  }

  public setTokenFunction(fn: GetTokenFn): void {
    this.getTokenFn = fn;
  }

  public async getToken(): Promise<string | null> {
    if (!this.getTokenFn) {
      console.warn('Token function not set in ClerkTokenGetter');
      return null;
    }
    return this.getTokenFn();
  }

  /**
   * Mints a new session token, bypassing Clerk's in-memory token cache. Used to
   * recover from a 401 caused by a stale cached token. Single-flight within one
   * session: concurrent callers share one request to Clerk, but a refresh begun
   * before a session boundary is never handed to a caller after it.
   */
  public getFreshToken(): Promise<string | null> {
    const getTokenFn = this.getTokenFn;
    if (!getTokenFn) {
      return Promise.resolve(null);
    }
    const currentEpoch = getSessionEpoch();
    if (this.freshTokenFlight && this.freshTokenFlight.epoch === currentEpoch) {
      return this.freshTokenFlight.promise;
    }
    const flight: { epoch: number; promise: Promise<string | null> } = {
      epoch: currentEpoch,
      promise: getTokenFn({ skipCache: true }).finally(() => {
        // An older flight finishing must not clear the newer session's one.
        if (this.freshTokenFlight === flight) {
          this.freshTokenFlight = null;
        }
      }),
    };
    this.freshTokenFlight = flight;
    return flight.promise;
  }
}

export const tokenGetter = ClerkTokenGetter.getInstance();
